import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import AWS from 'aws-sdk';
import { looksLikeHtml, makeExcerpt, postExcerpt } from '../utils/postContent';
import './BlogManagement.css';

const BlogManagement = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  // Configure AWS - using dedicated S3 uploader credentials
  const s3 = useMemo(() => {
    AWS.config.update({
      region: 'ca-central-1',
      accessKeyId: 'AKIARU2W6AM7HUJ4SQIY',
      secretAccessKey: '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'
    });
    return new AWS.S3();
  }, []);

  // Fetch all blog posts from S3 metadata
  const fetchPosts = useCallback(async () => {
    try {
      setIsLoadingPosts(true);
      
      // List objects in the metadata folder
      const listParams = {
        Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
        Prefix: 'blog-posts/metadata/'
      };

      const listResult = await s3.listObjectsV2(listParams).promise();
      const posts = [];

      // Fetch each metadata file
      for (const object of listResult.Contents || []) {
        try {
          const getParams = {
            Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
            Key: object.Key
          };
          
          const metadataResult = await s3.getObject(getParams).promise();
          const postData = JSON.parse(metadataResult.Body.toString());
          posts.push(postData);
        } catch (error) {
          console.error(`Error fetching metadata for ${object.Key}:`, error);
        }
      }

      // Sort posts by date (newest first)
      posts.sort((a, b) => new Date(b.date) - new Date(a.date));
      setPosts(posts);
    } catch (error) {
      console.error('Error fetching blog posts from S3:', error);
      
      // Fallback: Try to read from localStorage
      try {
        const localPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
        if (localPosts.length > 0) {
          // Fix blob URLs in existing posts
          const fixedPosts = localPosts.map(post => ({
            ...post,
            image: post.image && post.image.startsWith('blob:') ? '/images/1.png' : post.image
          }));
          
          // Update localStorage with fixed posts
          localStorage.setItem('blogPosts', JSON.stringify(fixedPosts));
          
          console.log('Using blog posts from localStorage (fixed):', fixedPosts);
          setPosts(fixedPosts);
        } else {
          console.log('No posts found in localStorage');
          setPosts([]);
        }
      } catch (localError) {
        console.error('Error reading from localStorage:', localError);
        setPosts([]);
      }
    } finally {
      setIsLoadingPosts(false);
    }
  }, [s3]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated, fetchPosts]);

  // Delete blog post from S3
  const handleDelete = async (postSlug) => {
    try {
      setIsSubmitting(true);
      
      // Delete metadata file from S3
      const deleteParams = {
        Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
        Key: `blog-posts/metadata/${postSlug}.json`
      };

      await s3.deleteObject(deleteParams).promise();
      setMessage('Blog post deleted successfully!');
      setDeleteConfirm(null);
      fetchPosts(); // Refresh the list
    } catch (error) {
      console.error('Error deleting blog post from S3:', error);
      
      // Fallback: Delete from localStorage
      try {
        const localPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
        const updatedPosts = localPosts.filter(post => post.slug !== postSlug);
        localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
        
        setMessage('Blog post deleted from localStorage!');
        setDeleteConfirm(null);
        fetchPosts(); // Refresh the list
      } catch (localError) {
        console.error('Error deleting from localStorage:', localError);
        setMessage('Error deleting blog post');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update blog post in S3
  const handleUpdate = async (postSlug, updatedData) => {
    try {
      setIsSubmitting(true);
      
      // Get the existing post data
      const existingPost = posts.find(post => post.slug === postSlug);
      if (!existingPost) {
        throw new Error('Post not found');
      }

      // Merge existing data with updates
      const updatedPost = {
        ...existingPost,
        ...updatedData,
        lastModified: new Date().toISOString()
      };

      // Save updated metadata to S3
      const uploadParams = {
        Bucket: 'chrystal-mousebound-site-prod-113462084414-ca-central-1',
        Key: `blog-posts/metadata/${postSlug}.json`,
        Body: JSON.stringify(updatedPost, null, 2),
        ContentType: 'application/json'
      };

      await s3.upload(uploadParams).promise();
      setMessage('Blog post updated successfully!');
      setEditingPost(null);
      fetchPosts(); // Refresh the list
    } catch (error) {
      console.error('Error updating blog post in S3:', error);
      
      // Fallback: Update in localStorage
      try {
        const localPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
        const updatedPosts = localPosts.map(post => 
          post.slug === postSlug 
            ? { ...post, ...updatedData, lastModified: new Date().toISOString() }
            : post
        );
        localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
        
        setMessage('Blog post updated in localStorage!');
        setEditingPost(null);
        fetchPosts(); // Refresh the list
      } catch (localError) {
        console.error('Error updating in localStorage:', localError);
        setMessage('Error updating blog post');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Edit form component
  const EditForm = ({ post, onSave, onCancel }) => {
    // Older posts kept the whole article in `excerpt`. Open them already split
    // so saving migrates the post to content + a short summary.
    const isLegacy = !(post.content || '').trim() && looksLikeHtml(post.excerpt);
    const [formData, setFormData] = useState({
      title: post.title,
      excerpt: isLegacy ? postExcerpt(post, 220) : post.excerpt || '',
      content: isLegacy ? post.excerpt : post.content || '',
      category: post.category,
      featured: post.featured || false
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(post.slug, {
        ...formData,
        excerpt: formData.excerpt.trim()
          ? makeExcerpt(formData.excerpt, 220)
          : makeExcerpt(formData.content, 220)
      });
    };

    return (
      <div className="edit-form-overlay">
        <div className="edit-form">
          <h3>Edit Blog Post</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Article</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows="14"
                required
              />
              <small className="form-hint">
                The full post — HTML or Markdown.
                {isLegacy && ' Moved here from the summary field; saving will store it correctly.'}
              </small>
            </div>
            <div className="form-group">
              <label>Card summary</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                rows="3"
              />
              <small className="form-hint">
                Optional — generated from the article's first paragraph if left blank.
              </small>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                <option value="Resorts">Resorts</option>
                <option value="Dining">Dining</option>
                <option value="Parks">Parks</option>
                <option value="Tips">Tips</option>
                <option value="News">News</option>
                <option value="Sales">Sales</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                />
                Featured Post
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" className="btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>Access Denied</h1>
          <p>Please log in to access the blog management panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-management-page">
      <section className="management-hero">
        <div className="container">
          <h1>Blog Management</h1>
          <p>Manage your Disney travel blog posts</p>
        </div>
      </section>

      <section className="management-content">
        <div className="container">
          <div className="management-header">
            <h2>All Blog Posts</h2>
            <button onClick={fetchPosts} className="refresh-btn" disabled={isLoadingPosts}>
              {isLoadingPosts ? 'Loading...' : '🔄 Refresh'}
            </button>
          </div>

          {message && (
            <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
              {message}
            </div>
          )}

          {isLoadingPosts ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading blog posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="no-posts">
              <h3>No blog posts found</h3>
              <p>Create your first blog post in the Admin panel!</p>
            </div>
          ) : (
            <div className="posts-table-container">
              <table className="posts-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Featured</th>
                    <th>PDF</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.slug}>
                      <td>
                        <div className="post-title">
                          <strong>{post.title}</strong>
                          <small>{makeExcerpt(post.excerpt || post.content, 60)}</small>
                        </div>
                      </td>
                      <td>
                        <span className={`category-badge ${post.category.toLowerCase()}`}>
                          {post.category}
                        </span>
                      </td>
                      <td>{formatDate(post.date)}</td>
                      <td>
                        {post.featured ? (
                          <span className="featured-badge">⭐ Featured</span>
                        ) : (
                          <span className="not-featured">—</span>
                        )}
                      </td>
                      <td>
                        {post.pdf_url ? (
                          <span className="pdf-badge">📄 PDF</span>
                        ) : (
                          <span className="no-pdf">—</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => setEditingPost(post)}
                            disabled={isSubmitting}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => setDeleteConfirm(post.slug)}
                            disabled={isSubmitting}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Edit Form Modal */}
      {editingPost && (
        <EditForm
          post={editingPost}
          onSave={handleUpdate}
          onCancel={() => setEditingPost(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this blog post? This action cannot be undone.</p>
            <div className="confirm-actions">
              <button
                className="btn-delete-confirm"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement; 